import React, { useState, useEffect } from 'react';
import { DollarSign, CreditCard, ExternalLink, Loader2, Plus, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import './WalletManagement.css';

const WalletManagement = () => {
  const { user } = useAuth();
  const { balance, loading, refreshWalletBalance } = useWallet();
  const [creatingPortal, setCreatingPortal] = useState(false);
  const [error, setError] = useState(null);
  
  // Top-up states
  const [showTopUpForm, setShowTopUpForm] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [creatingTopUp, setCreatingTopUp] = useState(false);
  const [topUpError, setTopUpError] = useState(null);
  const [topUpSuccess, setTopUpSuccess] = useState(false);

  // Refresh wallet balance
  const handleRefreshBalance = async () => {
    try {
      await refreshWalletBalance();
      setError(null);
    } catch (err) {
      console.error('Error refreshing wallet balance:', err);
      setError('Failed to refresh wallet balance');
    }
  };

  // Create Stripe Customer Portal session
  const createPortalSession = async () => {
    try {
      setCreatingPortal(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/wallet/create-portal-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Customer Portal
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create billing portal session');
      }
    } catch (err) {
      console.error('Error creating portal session:', err);
      setError('Failed to create billing portal session');
    } finally {
      setCreatingPortal(false);
    }
  };

  // Handle wallet top-up with Payment Links
  const handleTopUp = async (e) => {
    e.preventDefault();
    setCreatingTopUp(true);
    setTopUpError(null);
    setTopUpSuccess(false);
    
    try {
      const amountCents = Math.round(parseFloat(topUpAmount) * 100);
      
      if (amountCents < 100) {
        setTopUpError('Minimum amount is $1.00');
        return;
      }
      
      if (amountCents > 100000) {
        setTopUpError('Maximum amount is $1,000.00');
        return;
      }
      
      const token = localStorage.getItem('token');
      
      // Create payment link
      const response = await fetch('/api/wallet/create-payment-link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount_cents: amountCents })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment link');
      }
      
      const { url } = await response.json();
      
      // Redirect to Stripe Payment Link
      window.location.href = url;
      
    } catch (err) {
      setTopUpError(err.message || 'Failed to create payment link');
      console.error('Error creating payment link:', err);
    } finally {
      setCreatingTopUp(false);
    }
  };

  // Load wallet balance on component mount and refresh on focus
  useEffect(() => {
    // Refresh balance when component mounts
    handleRefreshBalance();
    
    // Check for payment success in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const amount = urlParams.get('amount');
    
    if (payment === 'success' && amount) {
      setTopUpSuccess(true);
      setTopUpAmount('');
      setShowTopUpForm(false);
      
      // Reload wallet balance to show updated amount
      handleRefreshBalance();
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Hide success message after 5 seconds
      setTimeout(() => setTopUpSuccess(false), 5000);
    } else if (payment === 'cancelled') {
      setTopUpError('Payment was cancelled');
      setShowTopUpForm(false);
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Hide error message after 5 seconds
      setTimeout(() => setTopUpError(null), 5000);
    }
    
    // Refresh balance when user returns to the page
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleRefreshBalance();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const formatBalance = (cents) => {
    return (cents / 100).toFixed(2);
  };

  return (
    <div className="wallet-management">
      <div className="wallet-header">
        <h3>Your Wallet</h3>
        <p>Manage your account balance and billing information</p>
      </div>

      <div className="wallet-content">
        {/* Wallet Balance */}
        <div className="wallet-balance-card">
          <div className="balance-header">
            <DollarSign className="balance-icon" />
            <span className="balance-label">Current Balance</span>
            <button 
              onClick={handleRefreshBalance} 
              className="refresh-button"
              title="Refresh balance"
            >
              <Loader2 className="refresh-icon" />
            </button>
          </div>
          <div className="balance-amount">
            {loading ? (
              <div className="loading-balance">
                <Loader2 className="spinner" />
                <span>Loading...</span>
              </div>
            ) : (
              <span className="balance-value">${formatBalance(balance)}</span>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={handleRefreshBalance} className="retry-button">
              Retry
            </button>
          </div>
        )}

            {/* Top-up Section */}
            <div className="topup-section">
              <h4>Add Funds to Wallet</h4>
              <p>Add money to your wallet to pay for agent interactions and premium features.</p>
              
              {!showTopUpForm ? (
                <button
                  onClick={() => setShowTopUpForm(true)}
                  className="add-funds-button"
                  disabled={loading}
                >
                  <Plus size={16} />
                  Add Funds
                </button>
              ) : (
                <form onSubmit={handleTopUp} className="topup-form">
                  <div className="form-group">
                    <label htmlFor="amount">Amount (USD)</label>
                    <div className="input-group">
                      <span className="currency-symbol">$</span>
                      <input
                        type="number"
                        id="amount"
                        value={topUpAmount}
                        onChange={(e) => setTopUpAmount(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="1.00"
                        max="1000.00"
                        required
                        disabled={creatingTopUp}
                      />
                    </div>
                    <small className="amount-hint">Minimum: $1.00 | Maximum: $1,000.00</small>
                  </div>
                  
                  {topUpError && (
                    <div className="error-message">
                      <span>{topUpError}</span>
                    </div>
                  )}
                  
                  {topUpSuccess && (
                    <div className="success-message">
                      <Check size={16} />
                      <span>Funds added successfully!</span>
                    </div>
                  )}
                  
                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setShowTopUpForm(false);
                        setTopUpAmount('');
                        setTopUpError(null);
                        setTopUpSuccess(false);
                      }}
                      className="cancel-button"
                      disabled={creatingTopUp}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creatingTopUp || !topUpAmount}
                      className="confirm-button"
                    >
                      {creatingTopUp ? (
                        <>
                          <Loader2 className="spinner" />
                          Creating Payment...
                        </>
                      ) : (
                        <>
                          <CreditCard size={16} />
                          Pay with Stripe
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>

        {/* Billing Management */}
        <div className="billing-section">
          <h4>Billing & Payment Methods</h4>
          <p>Manage your payment methods, view invoices, and update billing information through Stripe's secure portal.</p>
          
          <button
            onClick={createPortalSession}
            disabled={creatingPortal || loading}
            className="manage-billing-button"
          >
            {creatingPortal ? (
              <>
                <Loader2 className="spinner" />
                Opening Portal...
              </>
            ) : (
              <>
                <CreditCard size={16} />
                Manage Billing
                <ExternalLink size={14} />
              </>
            )}
          </button>
        </div>

        {/* Usage Information */}
        <div className="usage-info">
          <h4>How it works</h4>
          <ul>
            <li>Add funds to your wallet to pay for agent interactions</li>
            <li>Funds are automatically deducted when you use paid agents</li>
            <li>View your transaction history in the billing portal</li>
            <li>All payments are processed securely through Stripe</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WalletManagement;
